import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// Environments
import { environment } from '../../../environments/environment';

// Services
import { NgxPermissionsService } from 'ngx-permissions';

// RxJS
import { from, switchMap } from 'rxjs';

// Models
import { UtilizationResponse } from '../models/utilization-response.model';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  private readonly http = inject(HttpClient);
  private readonly permissionsSrv = inject(NgxPermissionsService);

  getUtilizationData(fromDate: string, toDate: string) {
    const interval = this.getInterval(fromDate, toDate);

    return from(this.permissionsSrv.hasPermission('ROLE_MAD')).pipe(
      switchMap(hasPermission => {
        const request$ = hasPermission
          ? this.utilizationData(fromDate, toDate, interval)
          : this.utilizationDataAdmin(fromDate, toDate, interval);
        return request$;
      })
    );
  }

  exportReport(fromDate: string, toDate: string, format: 'csv' | 'excel', contexts?: string[]) {
    return from(this.permissionsSrv.hasPermission('ROLE_MAD')).pipe(
      switchMap(hasPermission => {
        const endpoint = hasPermission
          ? '/monitoring/api/v1/report/export'
          : '/monitoring/api/v1/report/admin/export';

        return this.buildReportRequest(endpoint, fromDate, toDate, format, contexts);
      })
    );
  }

  private buildReportRequest(
    endpoint: string,
    fromDate: string,
    toDate: string,
    format: 'csv' | 'excel',
    contexts?: string[]
  ) {
    const interval = this.getInterval(fromDate, toDate);
    const body: any = { fromDate, toDate, interval };
    if (contexts?.length) {
      body.contexts = contexts;
    }

    const params = new HttpParams().set('format', format);

    return this.http.post(`${environment.apiGatewayUrl}${endpoint}`, body, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  private utilizationData(fromDate: string, toDate: string, interval: "DAY" | "WEEK" | "MONTH" | "YEAR") {
    return this.http.post<UtilizationResponse>(`${environment.apiGatewayUrl}/monitoring/api/v1/report`, { fromDate, toDate, interval });
  }

  private utilizationDataAdmin(fromDate: string, toDate: string, interval: "DAY" | "WEEK" | "MONTH" | "YEAR") {
    return this.http.post<UtilizationResponse>(`${environment.apiGatewayUrl}/monitoring/api/v1/report/admin`, { fromDate, toDate, interval });
  }

  private getInterval(fromDate: string, toDate: string): "DAY" | "WEEK" | "MONTH" | "YEAR" {
    const fromDt = new Date(fromDate);
    const toDt = new Date(toDate);
    const diffTime = Math.abs(toDt.getTime() - fromDt.getTime());
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    if (diffDays > 730) return "YEAR";
    if (diffDays > 180) return "MONTH";
    if (diffDays > 60) return "WEEK";
    return "DAY";
  }
}
